import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Heart, MessageCircle, Users, Scale } from 'lucide-react';
import RelationshipAssessmentViewer from '../components/assessment/RelationshipAssessmentViewer';
import RelationshipHealthCheck from '../data/assessments/RelationshipHealthCheck';
import CommunicationStyleProfile from '../data/assessments/CommunicationStyleProfile';
import RelationshipPatternsProfile from '../data/assessments/RelationshipPatternsProfile';
import ValuesAlignmentProfile from '../data/assessments/ValuesAlignmentProfile';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';
import { JOIN_PATH, takeJoinCode } from '../services/joinLink';
import { loadPlace } from '../services/sharedSessionPlace';

// The assessment open in this tab, so a reload reopens it (and a partner
// session resumes; see services/sharedSessionPlace.js). Cleared when the
// viewer closes through the app, which a reload doesn't do.
const OPEN_KEY = 'relationshipCompass_openAssessment';
const readOpen = () => {
  try {
    return sessionStorage.getItem(OPEN_KEY);
  } catch {
    return null;
  }
};

const AssessmentPage = () => {
  // A partner arriving via a share link or QR code lands on
  // /assessment/values-alignment/join/:sessionCode — go straight to the
  // Values Alignment assessment with the join dialog open and the code
  // pre-filled. Codes are 6-8 characters (older sessions used 6).
  // main.jsx normally moves the code out of the URL before analytics starts
  // (so it never reaches a page view), leaving the code-free join path.
  const { sessionCode: codeFromPath } = useParams();
  const { pathname } = useLocation();
  const rawJoinCode = codeFromPath || (pathname.replace(/\/$/, '') === JOIN_PATH ? takeJoinCode() : null);
  const joinCode = rawJoinCode
    ? rawJoinCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
    : null;
  const [selectedAssessment, setSelectedAssessment] = useState(rawJoinCode ? 'valuesAlignment' : readOpen);
  const { assessmentResults } = useApp();
  const sharedPlace = loadPlace(ValuesAlignmentProfile.id);

  useEffect(() => {
    if (!selectedAssessment) return undefined;
    try {
      sessionStorage.setItem(OPEN_KEY, selectedAssessment);
    } catch {
      /* storage blocked: a reload shows the list, as before */
    }
    return () => {
      try {
        sessionStorage.removeItem(OPEN_KEY);
      } catch {
        /* nothing to clear */
      }
    };
  }, [selectedAssessment]);

  // Assessment configurations
  const assessments = [
    {
      key: 'healthCheck',
      data: RelationshipHealthCheck,
      icon: Heart,
      color: 'rose',
      gradient: 'from-rose-500 to-pink-500',
      description: 'Quick comprehensive snapshot of your relationship across 8 key dimensions'
    },
    {
      key: 'communicationStyle',
      data: CommunicationStyleProfile,
      icon: MessageCircle,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-500',
      description: 'Understand your communication patterns and how your styles interact'
    },
    {
      key: 'relationshipPatterns',
      data: RelationshipPatternsProfile,
      icon: Users,
      color: 'purple',
      gradient: 'from-purple-500 to-pink-500',
      description: 'Identify recurring patterns, roles, and interaction cycles in your relationship'
    },
    {
      key: 'valuesAlignment',
      data: ValuesAlignmentProfile,
      icon: Scale,
      color: 'amber',
      gradient: 'from-amber-500 to-orange-500',
      description: 'Explore where your values align and diverge, and why that matters'
    }
  ];

  // If an assessment is selected, show the viewer
  if (selectedAssessment) {
    const assessment = assessments.find(a => a.key === selectedAssessment);
    return (
      <RelationshipAssessmentViewer
        assessment={assessment.data}
        assessmentKey={assessment.key}
        initialJoinCode={assessment.key === 'valuesAlignment' ? joinCode : null}
      />
    );
  }

  // Otherwise, show the assessment hub
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-16 pt-40">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          {/* Hero Image */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-block"
          >
            <img
              src="/images/assessment.png"
              alt="Assessment Hub"
              className="w-60 h-60 md:w-80 md:h-80 object-contain rounded-2xl mx-auto"
            />
          </motion.div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 px-4">
            Assessment Hub
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
            Choose an assessment to understand your relationship from different angles.
            Each assessment reveals unique insights and provides personalised recommendations.
          </p>
        </motion.div>

        {/* A partner session this device is part of */}
        {sharedPlace && (
          <Card className="p-5 mb-8 max-w-3xl mx-auto border-amber-500/40 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <p className="text-slate-200">
              {sharedPlace.step === 'results'
                ? 'Your shared Values Alignment session'
                : "You're partway through a shared Values Alignment session"}{' '}
              (code <span className="font-mono text-amber-300">{sharedPlace.sessionCode}</span>).
            </p>
            <Button onClick={() => setSelectedAssessment('valuesAlignment')} className="shrink-0">
              {sharedPlace.step === 'results' ? 'See results' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        )}

        {/* Assessment Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {assessments.map((assessment, idx) => {
            const Icon = assessment.icon;
            const isCompleted = assessmentResults?.[assessment.key];

            return (
              <motion.div
                key={assessment.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="p-4 sm:p-6 hover:border-slate-600 transition-all group cursor-pointer h-full flex flex-col">
                  <div className="flex-1">
                    {/* Icon and Status */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${assessment.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                      </div>
                      {isCompleted && (
                        <div className="flex items-center gap-1 text-green-400 text-sm font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Completed</span>
                        </div>
                      )}
                    </div>

                    {/* Title and Details */}
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                      {assessment.data.title}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-400 mb-4">
                      {assessment.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                      <span>⏱️ {assessment.data.duration}</span>
                      <span>•</span>
                      <span>📝 {assessment.data.questionCount} questions</span>
                    </div>

                    {/* Last Taken */}
                    {isCompleted && (
                      <div className="text-xs text-slate-400 mb-4">
                        Last taken: {new Date(isCompleted.dateTaken).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <Button
                    onClick={() => setSelectedAssessment(assessment.key)}
                    className="w-full"
                    variant={isCompleted ? 'secondary' : 'primary'}
                  >
                    {isCompleted ? 'Retake Assessment' : 'Start Assessment'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50">
            <h3 className="text-xl font-bold text-white mb-4">
              About These Assessments
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-slate-300">
              <div>
                <h4 className="font-semibold text-white mb-2">Why Take Multiple Assessments?</h4>
                <p className="text-sm">
                  Each assessment explores your relationship from a different angle. Together, they provide
                  a comprehensive understanding of your relationship health, communication dynamics, patterns,
                  and value compatibility.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Partner Comparison</h4>
                <p className="text-sm">
                  You can complete assessments individually, then compare results with your partner.
                  This reveals where you align and where perspectives differ, creating opportunities
                  for meaningful dialogue.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Track Changes Over Time</h4>
                <p className="text-sm">
                  Retake assessments periodically to see how your relationship evolves. Progress
                  tracking helps you celebrate improvements and identify areas that need attention.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Personalised Recommendations</h4>
                <p className="text-sm">
                  Based on your results, you'll receive tailored module recommendations and
                  specific practices to strengthen your relationship in the areas that matter most.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AssessmentPage;